/* Masidas theme — interactions. No dependencies. */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {}, cartType: 'drawer', delivery: {} };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Runs fn on the next frame, or after 120ms if the browser is not drawing frames (background tabs)
  const nextFrame = (fn) => { let done = false; const run = () => { if (!done) { done = true; fn(); } }; requestAnimationFrame(run); setTimeout(run, 120); };

  /* ---------- Money: same output as Liquid's money_without_trailing_zeros ---------- */
  function formatMoney(cents) {
    const fmt = theme.moneyFormat || '${{amount}}';
    const n = Number(cents) / 100;
    const group = (num, decimals, thousands, separator) => {
      const [i, d] = num.toFixed(decimals).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (d ? separator + d : '');
    };
    const out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(n, 0, ',', '.');
        case 'amount_with_comma_separator': return group(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(n, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(n, 2, "'", '.');
        default: return group(n, 2, ',', '.');
      }
    });
    return out.replace(/([.,])00(?!\d)/, '');
  }
  const addLabel = (cents) => (theme.strings.addToCartPrice ? theme.strings.addToCartPrice.replace('[price]', formatMoney(cents)) : theme.strings.addToCart);

  /* ---------- Toast ---------- */
  const toastEl = () => $('[data-toast]');
  function toast(message) {
    const el = toastEl();
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toast.timer);
    toast.timer = setTimeout(() => el.classList.remove('is-visible'), 2400);
  }

  /* ---------- Panels: drawers, search, size guide ---------- */
  const panels = {
    active: null,
    lastFocus: null,
    overlay: () => $('[data-overlay]'),
    open(id, trigger) {
      const panel = document.getElementById(id);
      if (!panel) return false;
      if (this.active && this.active !== panel) this.close(true);
      this.lastFocus = trigger || document.activeElement;
      panel.hidden = false;
      // ux: force a style flush so the slide-in still animates, but the panel is visible (and focusable)
      // inside the tap itself. iOS only raises the keyboard for a focus() made during the user gesture.
      void panel.offsetWidth;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      // ux: every panel, search included, dims and blurs the page behind it
      const ov = this.overlay();
      if (ov) { ov.hidden = false; void ov.offsetWidth; ov.classList.add('is-visible'); }
      document.body.classList.add('scroll-lock');
      $$(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      this.active = panel;
      document.dispatchEvent(new CustomEvent('panel:open', { detail: { id } }));
      const target = panel.querySelector('[data-autofocus]') || panel.querySelector('input:not([type=hidden]), [tabindex="-1"], button, a');
      if (target) target.focus({ preventScroll: true });
      return true;
    },
    close(silent) {
      const panel = this.active;
      if (!panel) return;
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      $$(`[aria-controls="${panel.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      const ov = this.overlay();
      if (ov) { ov.classList.remove('is-visible'); setTimeout(() => { if (!this.active) ov.hidden = true; }, 320); }
      document.body.classList.remove('scroll-lock');
      setTimeout(() => { if (this.active !== panel) panel.hidden = true; }, 460);
      this.active = null;
      document.dispatchEvent(new CustomEvent('panel:close', { detail: { id: panel.id } }));
      if (!silent && this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      const id = opener.getAttribute('data-open');
      if (id === 'CartDrawer' && theme.cartType !== 'drawer') return; // fall back to the cart page link
      if (document.getElementById(id)) { e.preventDefault(); panels.open(id, opener); }
      return;
    }
    if (e.target.closest('[data-close]') || e.target.matches('[data-overlay]')) { e.preventDefault(); panels.close(); }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (panels.active) panels.close();
      $$('details.dropdown[open]').forEach((d) => d.removeAttribute('open'));
    }
    if (e.key === 'Tab' && panels.active) {
      const focusables = $$('a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select, textarea, [tabindex="0"]', panels.active).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  // Close desktop dropdowns when clicking elsewhere
  document.addEventListener('click', (e) => {
    $$('details.dropdown[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
  });

  /* ---------- ux: menu drawer portal, nav fit, hover dropdowns ---------- */
  // The sticky header is its own stacking context (z-index 40). A drawer inside it can never rise above the
  // page overlay (z-index 60), so taps on menu links hit the overlay and close the menu. Move it to <body>.
  function initMenuPortal(wrap) {
    const fresh = wrap.querySelector('[data-portal]');
    if (!fresh) return;
    $$(`body > #${fresh.id}`).forEach((old) => { if (old !== fresh) { if (panels.active === old) panels.close(true); old.remove(); } });
    document.body.appendChild(fresh);
  }

  // If the links don't fit beside the logo (long labels, narrow laptop), fall back to the Menu button.
  function initNavFit(wrap) {
    const header = wrap.querySelector('.header');
    const nav = wrap.querySelector('.header__nav');
    const list = wrap.querySelector('.header__links');
    if (!header || !nav || !list) return;
    const check = () => {
      header.classList.remove('header--compact');
      if (window.matchMedia('(max-width: 990px)').matches) return;
      if (list.scrollWidth > nav.clientWidth + 1) header.classList.add('header--compact');
    };
    check();
    window.addEventListener('resize', debounce(check, 150));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(check);
  }

  // Desktop dropdowns open on hover as well as on click, and close when the pointer leaves.
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  document.addEventListener('mouseover', (e) => {
    if (!canHover.matches) return;
    const d = e.target.closest('details[data-hover-dropdown]');
    if (!d || d.open) return;
    $$('details[data-hover-dropdown][open]').forEach((o) => { if (o !== d) o.removeAttribute('open'); });
    clearTimeout(d._leave);
    d.open = true;
    d._hoverOpened = Date.now();
  });
  // A click right after a hover-open would toggle the dropdown shut; keep it open instead.
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('details[data-hover-dropdown] > summary');
    if (!summary) return;
    const d = summary.parentElement;
    if (d.open && d._hoverOpened && Date.now() - d._hoverOpened < 1500) { e.preventDefault(); d._hoverOpened = 0; }
  });
  document.addEventListener('mouseout', (e) => {
    if (!canHover.matches) return;
    const d = e.target.closest('details[data-hover-dropdown]');
    if (!d || d.contains(e.relatedTarget)) return;
    clearTimeout(d._leave);
    d._leave = setTimeout(() => d.removeAttribute('open'), 180);
  });
  document.addEventListener('mouseover', (e) => {
    const d = e.target.closest('details[data-hover-dropdown]');
    if (d) clearTimeout(d._leave);
  });
  document.addEventListener('focusout', (e) => {
    const d = e.target.closest('details[data-hover-dropdown]');
    if (d && !d.contains(e.relatedTarget)) d.removeAttribute('open');
  });

  /* ---------- Header: hide on scroll, overlay becomes solid ---------- */
  function initHeader() {
    const wrap = $('.section-header');
    if (!wrap || wrap.dataset.ready) return;
    wrap.dataset.ready = 'true';
    initMenuPortal(wrap);
    initNavFit(wrap);
    const hide = !!wrap.querySelector('[data-hide-on-scroll]');
    const overlay = !!wrap.querySelector('.header--overlay');
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      const goingDown = y > lastY;
      if (hide) {
        const shouldHide = goingDown && y > 240 && !panels.active;
        wrap.classList.toggle('is-hidden', shouldHide);
        document.body.classList.toggle('header-hidden', shouldHide);
      }
      wrap.classList.toggle('is-scrolled', y > 8); // ux: frosted header once the page moves
      if (overlay) wrap.classList.toggle('is-solid', y > 40 || !!panels.active || wrap.matches(':hover') || wrap.matches(':focus-within'));
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; nextFrame(update); } }, { passive: true });
    if (overlay) {
      ['mouseenter', 'mouseleave', 'focusin', 'focusout'].forEach((ev) => wrap.addEventListener(ev, () => nextFrame(update)));
      document.addEventListener('panel:open', update);
      document.addEventListener('panel:close', () => setTimeout(update, 50));
    }
    update();
  }

  /* ---------- Cart ---------- */
  const cart = {
    sectionIds() {
      const ids = ['cart-count'];
      if (document.getElementById('shopify-section-cart-drawer')) ids.push('cart-drawer');
      const main = $('[data-main-cart]');
      if (main) ids.push(main.dataset.sectionId);
      return ids;
    },
    async add(body) {
      body.append('sections', this.sectionIds().join(','));
      body.append('sections_url', window.location.pathname);
      const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      this.render(data.sections);
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      this.render(data.sections);
      return data;
    },
    render(sections) {
      if (!sections) return;
      if (sections['cart-count'] !== undefined) {
        const count = parse(sections['cart-count']).body.textContent.trim();
        $$('[data-cart-count]').forEach((el) => {
          el.textContent = count;
          const wrap = el.closest("[data-cart-count-wrap]"); if (wrap) wrap.hidden = count === "0";
          el.classList.remove('bump');
          void el.offsetWidth;
          el.classList.add('bump');
        });
      }
      if (sections['cart-drawer']) {
        const fresh = parse(sections['cart-drawer']).getElementById('CartDrawer');
        const drawer = document.getElementById('CartDrawer');
        if (fresh && drawer) {
          const hadFocus = drawer.contains(document.activeElement);
          // Express checkout buttons initialise once; move the live ones into the fresh markup instead of re-rendering them
          const liveExpress = drawer.querySelector('[data-cart-express]');
          drawer.innerHTML = fresh.innerHTML;
          const slot = drawer.querySelector('[data-cart-express]');
          if (liveExpress && slot && liveExpress.childElementCount) slot.replaceWith(liveExpress);
          drawer.classList.remove('is-pending');
          if (hadFocus) { const h = drawer.querySelector('[data-autofocus]'); if (h) h.focus({ preventScroll: true }); }
        }
      }
      const main = $('[data-main-cart]');
      if (main && sections[main.dataset.sectionId]) {
        const fresh = parse(sections[main.dataset.sectionId]).querySelector('[data-main-cart]');
        if (fresh) main.innerHTML = fresh.innerHTML;
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },
    // Open the drawer straight away while the add request runs, so the tap feels instant
    openPending() {
      const drawer = document.getElementById('CartDrawer');
      if (theme.cartType !== 'drawer' || !drawer) return false;
      drawer.classList.add('is-pending');
      if (panels.active !== drawer) panels.open('CartDrawer');
      return true;
    },
    cancelPending() {
      const drawer = document.getElementById('CartDrawer');
      if (drawer) drawer.classList.remove('is-pending');
    },
    afterAdd() {
      const drawer = document.getElementById('CartDrawer');
      if (theme.cartType === 'drawer' && drawer) { if (panels.active !== drawer) panels.open('CartDrawer'); }
      else toast(theme.strings.addedToast);
    }
  };

  function setBusy(button, busy) {
    if (!button) return;
    if (busy) button.setAttribute('aria-busy', 'true'); else button.removeAttribute('aria-busy');
  }

  /* Quick add on product cards */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    const body = new FormData();
    const selectId = btn.getAttribute('data-quick-add-select');
    const select = selectId ? document.getElementById(selectId) : null;
    body.append('id', select && select.value ? select.value : btn.getAttribute('data-quick-add'));
    body.append('quantity', '1');
    btn.disabled = true;
    setBusy(btn, true);
    const inDrawer = !!btn.closest('#CartDrawer');
    if (!inDrawer) cart.openPending();
    try {
      await cart.add(body);
      const card = btn.closest('.card');
      cart.afterAdd(card ? (card.querySelector('.card__title') || {}).textContent : '');
    } catch (err) {
      cart.cancelPending();
      toast(err.message);
    } finally {
      if (document.contains(btn)) { btn.disabled = false; setBusy(btn, false); }
    }
  });

  /* Quantity and remove inside the drawer or cart page */
  const changeLine = async (control, quantity) => {
    const line = parseInt(control.getAttribute('data-line'), 10);
    const row = control.closest('.cart-line');
    if (row) row.classList.add('is-loading');
    try { await cart.change(line, quantity); }
    catch (err) { toast(err.message); if (row) row.classList.remove('is-loading', 'is-removing'); }
  };
  const stepTimers = new WeakMap();
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (step) {
      e.preventDefault();
      const input = step.parentElement.querySelector('input');
      const next = Math.max(0, parseInt(input.value || '0', 10) + parseInt(step.getAttribute('data-qty-step'), 10));
      input.value = next;
      clearTimeout(stepTimers.get(input));
      stepTimers.set(input, setTimeout(() => changeLine(step, parseInt(input.value || '0', 10)), 280));
      return;
    }
    const remove = e.target.closest('[data-remove-line]');
    if (remove) {
      e.preventDefault();
      const row = remove.closest('.cart-line');
      if (row) row.classList.add('is-removing');
      changeLine(remove, 0);
    }
  });
  document.addEventListener('change', debounce((e) => {
    const input = e.target.closest('.cart-line .qty input');
    if (input) changeLine(input, Math.max(0, parseInt(input.value || '0', 10)));
  }, 350));

  /* ---------- Product form ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.querySelector('[data-atc]');
      this.error = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const idInput = this.form.querySelector('[name="id"]');
      if (!idInput || !idInput.value) {
        const picker = document.querySelector(`variant-picker[data-section="${this.dataset.section}"]`);
        if (picker) picker.flagMissing();
        return;
      }
      if (this.button.getAttribute('aria-busy') === 'true') return;
      setBusy(this.button, true);
      if (this.error) this.error.textContent = '';
      const opened = cart.openPending();
      try {
        await cart.add(new FormData(this.form));
        this.button.dataset.state = 'added';
        const label = this.button.querySelector('.button__label');
        const previous = label ? label.textContent : '';
        if (label) label.textContent = theme.strings.added;
        setTimeout(() => { delete this.button.dataset.state; if (label) label.textContent = previous; }, 1600);
        cart.afterAdd(this.dataset.productTitle);
      } catch (err) {
        cart.cancelPending();
        if (opened && panels.active && panels.active.id === 'CartDrawer') panels.close(true);
        if (this.error) this.error.textContent = err.message;
        toast(err.message);
      } finally {
        setBusy(this.button, false);
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- Gallery: only the selected colour's photos, capped, with "View all" ---------- */
  function setLead(gallery) {
    const items = $$('.gallery__item', gallery);
    items.forEach((li) => li.classList.remove('is-lead'));
    const lead = items.find((li) => !li.hidden);
    if (lead) lead.classList.add('is-lead');
    const counter = gallery.parentElement.querySelector('.gallery__count');
    if (counter) counter.textContent = `1 / ${items.filter((li) => !li.hidden).length}`;
  }
  function filterGallery(gallery, color) {
    const cap = parseInt(gallery.dataset.cap || '8', 10);
    const items = $$('.gallery__item', gallery);
    const own = items.some((li) => li.dataset.color === color);
    let shown = 0;
    items.forEach((li) => {
      const match = !own || !li.dataset.color || li.dataset.color === color;
      if (match) shown++;
      li.hidden = !match || shown > cap;
      li.toggleAttribute('data-more', match && shown > cap);
      li.classList.remove('is-zoomed');
    });
    const more = gallery.parentElement.querySelector('[data-gallery-more]');
    if (more) {
      more.hidden = shown <= cap;
      more.textContent = (more.dataset.template || more.textContent).replace(/d+/, shown);
    }
    setLead(gallery);
    const lead = items.find((li) => !li.hidden);
    if (lead) { const img = lead.querySelector('img'); if (img) { img.loading = 'eager'; } lead.classList.remove('is-swapped'); void lead.offsetWidth; lead.classList.add('is-swapped'); }
    if (window.matchMedia('(min-width: 761px)').matches) {
      const top = gallery.getBoundingClientRect().top;
      if (top < 0) window.scrollBy({ top: top - 24, behavior: reduceMotion ? 'auto' : 'smooth' });
    } else {
      gallery.scrollTo({ left: 0, behavior: 'auto' });
    }
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-gallery-more]');
    if (!btn) return;
    const gallery = document.getElementById(btn.dataset.galleryMore);
    if (!gallery) return;
    $$('.gallery__item[data-more]', gallery).forEach((li) => { li.hidden = false; li.removeAttribute('data-more'); });
    btn.hidden = true;
    setLead(gallery);
  });

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[type="application/json"]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.section = this.dataset.section;
      this.addEventListener('change', () => this.onChange());
      if (this.dataset.colorIndex !== undefined) this.lastColor = this.selected()[Number(this.dataset.colorIndex)];
      this.updateAvailability();
    }
    selected() {
      return $$('fieldset', this).map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }
    missingFieldset() {
      return $$('fieldset', this).find((fs) => !fs.querySelector('input:checked'));
    }
    // Scroll to the first unchosen option and say what's needed right there, not only under the button
    flagMissing() {
      const missing = this.missingFieldset();
      if (!missing) return;
      const text = missing.dataset.chooseHint || theme.strings.chooseSize;
      const hint = document.getElementById(`BuyHint-${this.section}`);
      if (hint) hint.textContent = text;
      const inline = missing.querySelector('[data-option-hint]');
      if (inline) {
        inline.textContent = text;
        missing.classList.remove('is-flagged');
        void missing.offsetWidth;
        missing.classList.add('is-flagged');
      }
      missing.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      const first = missing.querySelector('input');
      if (first) first.focus({ preventScroll: true });
    }
    // Label for the main and sticky buttons while something is still unchosen
    chooseLabel() {
      const missing = this.missingFieldset();
      return missing ? missing.dataset.chooseLabel || theme.strings.chooseSize : '';
    }
    onChange() {
      const values = this.selected();
      $$('fieldset', this).forEach((fs, i) => {
        const label = fs.querySelector('[data-selected-value]');
        if (label) label.textContent = values[i] || '';
      });
      this.updateAvailability();
      const hint = document.getElementById(`BuyHint-${this.section}`);
      if (hint) hint.textContent = '';
      $$('fieldset', this).forEach((fs) => {
        if (fs.querySelector('input:checked')) {
          fs.classList.remove('is-flagged');
          const inline = fs.querySelector('[data-option-hint]');
          if (inline) inline.textContent = '';
        }
      });
      const button = document.querySelector(`#ProductForm-${this.section} [data-atc]`);
      const stickyBtn = document.querySelector(`[data-sticky-atc="${this.section}"] .button`);
      // A colour pick moves the gallery even before a size is chosen
      this.showColorMedia(values);
      if (values.includes(null)) {
        const label = this.chooseLabel();
        // Stay tappable so the tap can point to the missing option
        this.setButton(button, true, label);
        this.setButton(stickyBtn, true, label);
        return;
      }
      const variant = this.variants.find((v) => v.options.every((o, i) => o === values[i]));
      const idInput = document.querySelector(`#ProductForm-${this.section} [name="id"]`);
      if (!variant) {
        if (idInput) idInput.value = '';
        this.setButton(button, false, theme.strings.unavailable);
        this.setButton(stickyBtn, false, theme.strings.unavailable);
        return;
      }
      if (idInput) {
        idInput.value = variant.id;
        // Shop Pay Installments and other listeners read the form's id on change
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.setButton(button, variant.available, variant.available ? addLabel(variant.price) : theme.strings.soldOut);
      this.setButton(stickyBtn, variant.available, variant.available ? addLabel(variant.price) : theme.strings.soldOut);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      if (this.dataset.colorIndex === undefined) this.showMedia(variant);
      this.refreshPrice(variant.id);
    }
    setButton(button, enabled, text) {
      if (!button) return;
      button.disabled = !enabled;
      const label = button.querySelector('.button__label') || button;
      label.textContent = text;
    }
    updateAvailability() {
      const values = this.selected();
      $$('fieldset', this).forEach((fs, index) => {
        $$('input', fs).forEach((input) => {
          // A value is available if some in-stock variant has it, combined with the other options already chosen
          const available = this.variants.some((v) => v.available && v.options.every((o, i) => {
            if (i === index) return o === input.value;
            return values[i] === null || o === values[i];
          }));
          input.closest('.option__value').classList.toggle('option__value--unavailable', !available);
        });
      });
    }
    showColorMedia(values) {
      const ci = this.dataset.colorIndex;
      if (ci === undefined) return;
      const color = values[Number(ci)];
      if (!color || color === this.lastColor) return;
      this.lastColor = color;
      const gallery = document.getElementById(`Gallery-${this.section}`);
      if (gallery && gallery.hasAttribute('data-by-color')) { filterGallery(gallery, color); return; }
      // Prefer a variant that matches everything already chosen, then any variant in this colour
      const match = this.variants.find((v) => v.featured_media && v.options.every((o, i) => values[i] === null || o === values[i]))
        || this.variants.find((v) => v.featured_media && v.options[Number(ci)] === color);
      if (match) this.showMedia(match);
    }
    showMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = document.getElementById(`Gallery-${this.section}`);
      if (!gallery) return;
      const item = gallery.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (!item) return;
      if (window.matchMedia('(min-width: 761px)').matches) {
        if (gallery.firstElementChild !== item) {
          gallery.prepend(item);
          item.classList.remove('is-swapped');
          void item.offsetWidth;
          item.classList.add('is-swapped');
        }
        const top = gallery.getBoundingClientRect().top;
        if (top < 0) window.scrollBy({ top: top - 24, behavior: reduceMotion ? 'auto' : 'smooth' });
      } else {
        gallery.scrollTo({ left: item.offsetLeft - gallery.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }
    async refreshPrice(variantId) {
      try {
        const res = await fetch(`${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.sectionId}`);
        const doc = parse(await res.text());
        ['Price', 'Inventory', 'StickyPrice'].forEach((key) => {
          const fresh = doc.getElementById(`${key}-${this.section}`);
          const current = document.getElementById(`${key}-${this.section}`);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
      } catch (err) { /* price stays as is */ }
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Size guide: inches / cm ---------- */
  class SizeTable extends HTMLElement {
    connectedCallback() {
      this.cells = [];
      const table = this.querySelector('table');
      if (!table) return;
      const rows = $$('tr', table);
      const head = rows[0] ? $$('th, td', rows[0]).map((c) => c.textContent.trim().toLowerCase()) : [];
      // Size labels and size conversions (US, EU, UK) are not measurements
      const skip = head.map((h) => /^(size|us|eu|uk|au|it|fr|label)/.test(h));
      rows.slice(1).forEach((tr) => $$('td', tr).forEach((td, i) => {
        if (skip[i]) return;
        const raw = td.textContent.trim();
        if (/^\d+(\.\d+)?(\s*[-–/]\s*\d+(\.\d+)?)?$/.test(raw)) this.cells.push({ td, raw });
      }));
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-unit]');
        if (btn) this.setUnit(btn.dataset.unit);
      });
    }
    setUnit(unit) {
      $$('button[data-unit]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unit === unit)));
      this.cells.forEach(({ td, raw }) => {
        td.textContent = unit === 'cm' ? raw.replace(/\d+(\.\d+)?/g, (n) => String(Math.round(parseFloat(n) * 2.54))) : raw;
      });
    }
  }
  if (!customElements.get('size-table')) customElements.define('size-table', SizeTable);

  /* ---------- Gallery: zoom on desktop, counter on mobile ---------- */
  document.addEventListener('click', (e) => {
    const item = e.target.closest('.gallery__item[data-zoomable]');
    if (!item || !window.matchMedia('(hover: hover)').matches) return;
    item.classList.toggle('is-zoomed');
    if (item.classList.contains('is-zoomed')) {
      const img = item.querySelector('img');
      const rect = item.getBoundingClientRect();
      if (img) img.style.transformOrigin = `${((e.clientX - rect.left) / rect.width) * 100}% ${((e.clientY - rect.top) / rect.height) * 100}%`;
    }
  });
  document.addEventListener('mousemove', (e) => {
    const item = e.target.closest('.gallery__item.is-zoomed');
    if (!item) return;
    const img = item.querySelector('img');
    const rect = item.getBoundingClientRect();
    if (img) img.style.transformOrigin = `${((e.clientX - rect.left) / rect.width) * 100}% ${((e.clientY - rect.top) / rect.height) * 100}%`;
  });
  function initGalleryCounters() {
    $$('.gallery').forEach((gallery) => {
      const counter = gallery.parentElement.querySelector('.gallery__count');
      if (!counter) return;
      gallery.addEventListener('scroll', debounce(() => {
        const items = $$('.gallery__item', gallery).filter((li) => !li.hidden);
        const index = Math.round(gallery.scrollLeft / (items[0] ? items[0].offsetWidth : 1));
        counter.textContent = `${Math.min(index + 1, items.length)} / ${items.length}`;
      }, 60), { passive: true });
    });
  }

  /* ---------- Sticky add to bag ---------- */
  function initStickyAtc() {
    $$('[data-sticky-atc]').forEach((bar) => {
      const section = bar.getAttribute('data-sticky-atc');
      const main = document.querySelector(`#ProductForm-${section} [data-atc]`);
      if (!main || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        $$('button', bar).forEach((b) => { b.tabIndex = show ? 0 : -1; });
      });
      io.observe(main);
      bar.querySelector('.button').addEventListener('click', () => {
        const picker = document.querySelector(`variant-picker[data-section="${section}"]`);
        if (picker && picker.missingFieldset && picker.missingFieldset()) { picker.flagMissing(); return; }
        const form = document.getElementById(`ProductForm-${section}`);
        if (form) form.requestSubmit();
      });
    });
  }

  /* ---------- Delivery estimate ---------- */
  function addBusinessDays(date, days) {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return d;
  }
  function initDelivery() {
    const cfg = theme.delivery || {};
    $$('[data-delivery-estimate]').forEach((el) => {
      const now = new Date();
      const start = addBusinessDays(now, (cfg.processing || 0) + (cfg.min || 0));
      const end = addBusinessDays(now, (cfg.processing || 0) + (cfg.max || 0));
      let fmt;
      try { fmt = new Intl.DateTimeFormat(cfg.locale || undefined, { month: 'short', day: 'numeric' }); } catch (err) { fmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }); }
      let endText = fmt.format(end);
      if (start.getMonth() === end.getMonth()) { try { endText = new Intl.DateTimeFormat(cfg.locale || undefined, { day: 'numeric' }).format(end); } catch (err) { /* keep full date */ } }
      el.textContent = el.getAttribute('data-template').replace('[start]', fmt.format(start)).replace('[end]', endText);
    });
  }

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('#PredictiveResults');
      if (!this.input) return;
      this.input.addEventListener('input', debounce(() => this.search(this.input.value.trim()), 220));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.addEventListener('click', (e) => {
        const s = e.target.closest('[data-suggest]');
        if (s) { this.input.value = s.getAttribute('data-suggest'); this.search(this.input.value); this.input.focus(); }
      });
    }
    async search(q) {
      if (this.controller) this.controller.abort();
      const suggest = this.querySelector('[data-search-suggest]');
      if (suggest) suggest.hidden = !!q; // ux: popular searches only while the field is empty
      if (!q) { this.results.innerHTML = ''; this.input.setAttribute('aria-expanded', 'false'); return; }
      this.controller = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=8&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,tag&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) return;
        const doc = parse(await res.text());
        const fresh = doc.querySelector('[data-predictive]');
        this.results.innerHTML = fresh ? fresh.outerHTML : '';
        this.input.setAttribute('aria-expanded', 'true');
        this.index = -1;
      } catch (err) { /* aborted or offline */ }
    }
    onKey(e) {
      const options = $$('[role="option"]', this.results);
      if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      if (e.key === 'Enter') {
        if (this.index >= 0 && options[this.index]) { e.preventDefault(); options[this.index].querySelector('a').click(); }
        return;
      }
      e.preventDefault();
      this.index = typeof this.index === 'number' ? this.index : -1;
      this.index = (this.index + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.index)));
      this.input.setAttribute('aria-activedescendant', options[this.index].id);
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Collection and search filters, sort and load more ---------- */
  // ux: one form per results section (collections and search). Filters apply as you tick them; "Show results"
  // is a real submit button, so it flushes a pending update and then closes the drawer. Everything is
  // delegated from this element, so nothing is lost when the facet markup is re-rendered.
  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.basePath = this.dataset.url || window.location.pathname;
      this.timer = null;
      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-sort]')) return;
        if (!e.target.closest('form')) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => { this.timer = null; this.apply(this.queryFromForm()); }, 380);
      });
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-sort]')) { clearTimeout(this.timer); this.timer = null; this.apply(this.queryFromForm()); }
      });
      this.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (this.timer) { clearTimeout(this.timer); this.timer = null; await this.apply(this.queryFromForm()); }
        else if (this.pending) await this.pending.catch(() => {});
        if (panels.active && this.contains(panels.active)) panels.close();
      });
      // Remove-filter chips live in the toolbar and in the empty state, both inside the section
      this.closest('.shopify-section')?.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (!link) return;
        e.preventDefault();
        clearTimeout(this.timer); this.timer = null;
        this.apply(new URL(link.href, window.location.origin).searchParams.toString());
      });
    }
    get form() { return this.querySelector('form[data-facet-form]'); }
    queryFromForm() {
      const form = this.form;
      const data = form ? new FormData(form) : new FormData();
      const sort = this.querySelector('[data-sort]');
      if (sort && sort.value) data.set('sort_by', sort.value);
      const params = new URLSearchParams();
      for (const [k, v] of data.entries()) if (v !== '' && k !== 'page') params.append(k, v);
      return params.toString();
    }
    apply(query) {
      const run = this.fetchAndSwap(query);
      this.pending = run;
      return run;
    }
    async fetchAndSwap(query) {
      const results = document.getElementById(`CollectionResults-${this.sectionId}`);
      if (results) results.classList.add('is-loading');
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const res = await fetch(`${this.basePath}?${query}${query ? '&' : ''}section_id=${this.sectionId}`, { signal: this.controller.signal });
        const doc = parse(await res.text());
        const openIds = $$('details[open]', this).map((d) => d.id).filter(Boolean);
        const focusedName = document.activeElement && this.contains(document.activeElement) ? document.activeElement.getAttribute('data-focus-key') : null;
        ['CollectionResults', 'FacetsBody', 'ActiveFilters', 'ProductCount', 'FacetsCount'].forEach((key) => {
          const fresh = doc.getElementById(`${key}-${this.sectionId}`);
          const current = document.getElementById(`${key}-${this.sectionId}`);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        openIds.forEach((id) => { const d = document.getElementById(id); if (d) d.open = true; });
        if (focusedName) { const el = this.querySelector(`[data-focus-key="${CSS.escape(focusedName)}"]`); if (el) el.focus({ preventScroll: true }); }
        window.history.replaceState({}, '', `${this.basePath}${query ? '?' + query : ''}`);
      } catch (err) {
        if (err.name !== 'AbortError') toast(theme.strings.cartError);
      } finally {
        if (results) results.classList.remove('is-loading');
      }
    }
  }
  if (!customElements.get('facet-form')) customElements.define('facet-form', FacetForm);

  // Load more appends the next page in place. The address bar is left alone on purpose: writing ?page=2
  // meant a refresh or a shared link dropped everything on page 1.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-load-more]');
    if (!btn) return;
    e.preventDefault();
    const grid = document.getElementById(btn.getAttribute('data-grid'));
    const url = btn.getAttribute('data-next-url');
    if (!grid || !url) return;
    if (btn.getAttribute('aria-busy') === 'true') return;
    const hadFocus = document.activeElement === btn;
    setBusy(btn, true);
    try {
      const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${btn.getAttribute('data-section-id')}`);
      const doc = parse(await res.text());
      const freshGrid = doc.getElementById(grid.id);
      const added = freshGrid ? $$(':scope > *', freshGrid) : [];
      added.forEach((item) => grid.appendChild(item));
      const status = btn.closest('.load-more') && btn.closest('.load-more').querySelector('[data-load-status]');
      const freshStatus = doc.querySelector('[data-load-status]');
      if (status && freshStatus) status.textContent = freshStatus.textContent.replace(/^\s*\d+/, String(grid.children.length));
      const freshBtn = doc.querySelector('[data-load-more]');
      if (freshBtn) btn.setAttribute('data-next-url', freshBtn.getAttribute('data-next-url'));
      else if (btn.closest('.load-more')) btn.closest('.load-more').remove();
      // Move keyboard and screen reader users to the first new product
      const firstLink = added[0] && added[0].querySelector('a[href]');
      if (firstLink && hadFocus) firstLink.focus({ preventScroll: true });
    } catch (err) {
      window.location.href = url;
    } finally {
      setBusy(btn, false);
    }
  });

  /* ---------- ux: product cards (touch quick add, colour swatches) ---------- */
  // On touch screens the size panel is hidden until the "+" is tapped; tapping elsewhere closes it.
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-quick-toggle]');
    $$('.card.is-quick-open').forEach((c) => { if (!toggle || c !== toggle.closest('.card')) { c.classList.remove('is-quick-open'); const t = c.querySelector('[data-quick-toggle]'); if (t) t.setAttribute('aria-expanded', 'false'); } });
    if (!toggle) return;
    e.preventDefault();
    const card = toggle.closest('.card');
    const open = !card.classList.contains('is-quick-open');
    card.classList.toggle('is-quick-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      buildQuick(card.querySelector('quick-add[data-url]')).then(() => {
        const first = card.querySelector('.card__quick [data-quick-add]:not([disabled]), .card__quick a');
        if (first && card.classList.contains('is-quick-open')) first.focus({ preventScroll: true });
      });
    }
  });

  /* ---------- Quick add: option buttons are built on first hover/open ---------- */
  // Cards ship only a label; the buttons come from /products/<handle>.js so grids stay light.
  // Sizes list in wearing order. Same rules as snippets/size-key.liquid.
  const SIZE_RANKS = { '3xs': 1, xxxs: 1, '2xs': 2, xxs: 2, xs: 3, 'xs/s': 4, s: 5, small: 5, 's/m': 6, m: 7, medium: 7, 'm/l': 8, l: 9, large: 9, 'l/xl': 10, xl: 11, '1xl': 11, '1x': 11, xxl: 12, '2xl': 12, '2x': 12, xxxl: 13, '3xl': 13, '3x': 13, xxxxl: 14, '4xl': 14, '4x': 14, '5xl': 15, '5x': 15, '6xl': 16, '7xl': 17, '8xl': 18, '9xl': 19 };
  const sizeRank = (value, index) => {
    const v = String(value || '').toLowerCase().trim();
    const word = v.split(' ')[0].split('(')[0].trim();
    if (Object.prototype.hasOwnProperty.call(SIZE_RANKS, word)) return [1, SIZE_RANKS[word], index];
    const num = v.match(/d+(?:.d+)?/);
    if (num) return [2, parseFloat(num[0]), index];
    return [3, 0, index];
  };
  const bySizeKey = (a, b) => (a[0] - b[0]) || (a[1] - b[1]) || (a[2] - b[2]);
  theme.sizeRank = sizeRank;
  const JUNK_VALUES = ['n/a', 'na', 'none', 'null', '-', 'default', 'default title'];
  const productJson = new Map();
  const loadProductJson = (url) => {
    if (!productJson.has(url)) {
      productJson.set(url, fetch(`${url.split('?')[0]}.js`).then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
        .catch((err) => { productJson.delete(url); throw err; }));
    }
    return productJson.get(url);
  };
  const escapeHtml = (str) => String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  async function buildQuick(qa) {
    if (!qa || !qa.dataset.url) return;
    if (qa.__built) return qa.__built;
    qa.__built = (async () => {
      try {
        const product = await loadProductJson(qa.dataset.url);
        const pick = Number(qa.dataset.pick);
        const ci = qa.dataset.colorIndex !== undefined ? Number(qa.dataset.colorIndex) : null;
        const color = qa.dataset.color || '';
        const optName = ((product.options || [])[pick] || {}).name || '';
        const isSize = /size/i.test(optName);
        const rows = [];
        product.variants.forEach((v, i) => {
          if (ci !== null && color && v.options[ci] !== color) return;
          const val = v.options[pick];
          if (val == null || JUNK_VALUES.includes(String(val).toLowerCase().trim())) return;
          const existing = rows.find((r) => r.val === val);
          if (existing) { if (!existing.v.available && v.available) existing.v = v; return; }
          rows.push({ val, v, key: isSize ? sizeRank(val, i) : [0, 0, i] });
        });
        if (!rows.length) { delete qa.dataset.url; return; }
        rows.sort((a, b) => bySizeKey(a.key, b.key));
        const tpl = qa.dataset.aria || '%v';
        qa.insertAdjacentHTML('beforeend', rows.map((r) => `<button type="button" data-quick-add="${r.v.id}"${r.v.available ? '' : ' disabled'} aria-label="${escapeHtml(tpl.replace('%v', r.val))}">${escapeHtml(r.val)}</button>`).join(''));
        qa.classList.add('is-built');
      } catch (err) {
        qa.__built = null;
        // Offline or blocked: fall back to the product page
        if (!qa.querySelector('a')) qa.insertAdjacentHTML('beforeend', `<a class="card__quick-full" href="${qa.dataset.url}">${escapeHtml(theme.strings.chooseOptions || 'Choose options')}</a>`);
      }
    })();
    return qa.__built;
  }
  const warmQuick = (e) => {
    const card = e.target.closest && e.target.closest('.card');
    if (card) buildQuick(card.querySelector('quick-add[data-url]'));
  };
  document.addEventListener('mouseover', (e) => { if (canHover.matches) warmQuick(e); });
  document.addEventListener('focusin', warmQuick);
  // Hovering or focusing a colour dot previews that colour's photo on the card.
  const previewSwatch = (e) => {
    const dot = e.target.closest && e.target.closest('[data-swatch-src]');
    if (!dot) return;
    const card = dot.closest('.card');
    const img = card && card.querySelector('.card__img--main');
    if (!img) return;
    if (!img.dataset.originalSrcset) { img.dataset.originalSrcset = img.getAttribute('srcset') || ''; img.dataset.originalSrc = img.getAttribute('src') || ''; }
    img.setAttribute('srcset', dot.getAttribute('data-swatch-srcset') || '');
    img.setAttribute('src', dot.getAttribute('data-swatch-src'));
  };
  const resetSwatch = (e) => {
    const wrap = e.target.closest && e.target.closest('.card__swatches');
    if (!wrap || wrap.contains(e.relatedTarget)) return;
    const img = wrap.closest('.card').querySelector('.card__img--main');
    if (img && img.dataset.originalSrc) { img.setAttribute('srcset', img.dataset.originalSrcset); img.setAttribute('src', img.dataset.originalSrc); }
  };
  document.addEventListener('mouseover', (e) => { if (canHover.matches) previewSwatch(e); });
  document.addEventListener('focusin', previewSwatch);
  document.addEventListener('mouseout', resetSwatch);
  document.addEventListener('focusout', resetSwatch);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window)) { this.load(); return; }
      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) { io.disconnect(); this.load(); }
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
    async load() {
      try {
        const res = await fetch(this.dataset.url);
        const doc = parse(await res.text());
        const fresh = doc.querySelector('product-recommendations');
        const grid = fresh && fresh.querySelector('.recs__grid');
        if (grid) {
          // Same department only, no near-duplicates, nothing already shown higher on the page
          const shown = (window.__shownProducts = window.__shownProducts || new Set());
          if (this.dataset.productKey) shown.add(this.dataset.productKey);
          const limit = parseInt(this.dataset.limit || '4', 10);
          const dept = this.dataset.dept;
          const sameDept = this.dataset.sameDept !== 'false' && dept;
          let kept = 0;
          $$(':scope > li', grid).forEach((li) => {
            const key = li.dataset.key;
            const ok = kept < limit && !shown.has(key) && (!sameDept || li.dataset.dept === dept);
            if (ok) { shown.add(key); kept++; } else li.remove();
          });
        }
        const section = this.closest('.shopify-section');
        if (fresh && fresh.querySelector('.card')) this.innerHTML = fresh.innerHTML;
        else if (section) section.hidden = true;
      } catch (err) { /* leave section empty */ }
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Localization selects submit on change ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-localization-select]')) e.target.form.submit();
  });

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', () => { initHeader(); initStickyAtc(); initDelivery(); initGalleryCounters(); });
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });

  /* ---------- Boot ---------- */
  const boot = () => { initHeader(); initStickyAtc(); initDelivery(); initGalleryCounters(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* v3: horizontal scrollers + product tabs */
(() => {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-scroll]');
    if (btn) {
      const root = btn.closest('.hs-wrap') || btn.closest('section');
      const track = root && [...root.querySelectorAll('[data-hscroll]')].find((t) => t.offsetParent !== null);
      if (track) track.scrollBy({ left: Math.max(track.clientWidth * 0.8, 200) * Number(btn.dataset.scroll), behavior: 'smooth' });
      return;
    }
    const tab = e.target.closest('[data-pt-tab]');
    if (tab) {
      const sec = tab.closest('section');
      sec.querySelectorAll('[data-pt-tab]').forEach((b) => b.setAttribute('aria-selected', String(b === tab)));
      sec.querySelectorAll('.pt__panel').forEach((p) => { p.hidden = p.id !== tab.getAttribute('aria-controls'); });
      const all = sec.querySelector('[data-pt-all]'); if (all && tab.dataset.url) all.href = tab.dataset.url;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      loadTabPanel(panel && panel.querySelector('[data-pt-src]'));
    }
  });
  // Tabs after the first load their cards on first use (or on hover/focus of the tab, to feel instant)
  const loading = new WeakMap();
  function loadTabPanel(holder) {
    if (!holder) return Promise.resolve();
    if (loading.has(holder)) return loading.get(holder);
    holder.setAttribute('aria-busy', 'true');
    const src = holder.dataset.ptSrc;
    const joiner = src.includes('?') ? '&' : '?';
    const job = fetch(`${src}${joiner}section_id=product-tabs-panel`)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(r.status))))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const track = doc.querySelector('[data-hscroll]');
        if (!track) throw new Error('empty');
        const limit = parseInt(holder.dataset.limit || '12', 10);
        [...track.children].slice(limit).forEach((li) => li.remove());
        holder.replaceWith(track);
      })
      .catch(() => { loading.delete(holder); holder.removeAttribute('aria-busy'); });
    loading.set(holder, job);
    return job;
  }
  const warmTab = (e) => {
    const tab = e.target.closest && e.target.closest('[data-pt-tab]');
    if (!tab) return;
    const panel = document.getElementById(tab.getAttribute('aria-controls'));
    loadTabPanel(panel && panel.querySelector('[data-pt-src]'));
  };
  document.addEventListener('mouseover', warmTab);
  document.addEventListener('focusin', warmTab);
  document.addEventListener('touchstart', warmTab, { passive: true });
})();

/* r3: hero slideshow (first slide always first; autoplay off for reduced motion; pauses on hover/focus/hidden tab) */
(() => {
  document.querySelectorAll('[data-hero-slideshow]').forEach((root) => {
    const slides = [...root.querySelectorAll('.hs__slide')];
    if (slides.length < 2) return;
    const dots = [...root.querySelectorAll('[data-hs-go]')];
    const pauseBtn = root.querySelector('[data-hs-pause]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const interval = Math.max(4, Number(root.dataset.interval) || 8) * 1000;
    let i = 0, timer = null, userPaused = reduced, hover = false;
    const show = (n) => {
      i = (n + slides.length) % slides.length;
      slides.forEach((s, k) => {
        const on = k === i;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', String(!on));
        s.querySelectorAll('a.hb__btn').forEach((a) => { if (on) a.removeAttribute('tabindex'); else a.setAttribute('tabindex', '-1'); });
        if (on) s.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      });
      dots.forEach((d, k) => d.setAttribute('aria-selected', String(k === i)));
      // warm the next slide's images
      const next = slides[(i + 1) % slides.length];
      next.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    };
    const stop = () => { clearInterval(timer); timer = null; };
    const start = () => { stop(); if (!userPaused && !hover && !document.hidden) timer = setInterval(() => show(i + 1), interval); };
    dots.forEach((d) => d.addEventListener('click', () => { show(Number(d.dataset.hsGo)); start(); }));
    if (pauseBtn) {
      if (reduced) pauseBtn.setAttribute('aria-pressed', 'true');
      pauseBtn.addEventListener('click', () => {
        userPaused = !userPaused;
        pauseBtn.setAttribute('aria-pressed', String(userPaused));
        pauseBtn.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
        start();
      });
    }
    root.addEventListener('focusin', () => { hover = true; stop(); });
    root.addEventListener('focusout', () => { hover = false; start(); });
    document.addEventListener('visibilitychange', start);
    show(0);
    start();
  });
})();
